import {
  ConflictLabel,
  HardwareConflictRow,
  HardwareConflictsContext,
  HardwareConflictValues,
} from './HardwareConflictField.js';
import {
  FIELD_LABELS,
  normalizeCircuitBreakerSize,
  type HardwareConflict,
} from '@cer-autofill/shared';
import { defaultDcIsolator } from '../lib/hardware-rows.js';
/** Hardware edits stay in the report draft until Save as new revision. */
import { useEffect, useState } from 'react';
import {
  getPromptsForJob,
  HARDWARE_CHECK_LABELS,
  batteryInstallationLabel,
  batteryLabelUploadKey,
  batterySectionPhotoKeys,
  calculateArrayTotals,
  connectedInverterSerialSuggestion,
  inverterSectionPhotoKeys,
  labellingSuggestionCopy,
  resolveBatteryInverters,
  seedBtyBatteryRow,
  switchboardLabellingSuggestion,
  withBatteryRowEdit,
  type HardwareKind,
} from '@cer-autofill/shared';
import {
  acceptOnlyReviewLabel,
  mainSwitchBreakerPortalWarning,
  type AcceptOnlyReview,
} from '../lib/auto-hardware-checks.js';
import { SuggestionReviewMark } from './HardwareCheckFields.js';
import { fetchPhotoBlobUrl } from '../api.js';
import { ReportPhotoEditor, type ReportPhotoActions } from './ReportPhotoEditor.js';
import { canRotatePhoto } from './EditablePhotoTile.js';
import { quarterTurnDegrees } from './PhotoLightbox.js';
import { hardwareWithoutRow, removeHardwareRowPrompt } from '../lib/hardware-rows.js';
import type {
  BessSystemType,
  FieldHardware,
  FieldReport,
  ItpBatteryEntry,
  PvAcIsolatorEntry,
  PvDcIsolatorEntry,
  PvInverterEntry,
  PvModuleEntry,
  StoredPhotoManifest,
} from '@cer-autofill/shared';

const BATTERY_SYSTEM_TYPES: [BessSystemType, string][] = [
  ['integrated', 'Pre-assembled Integrated BESS (AS/NZS 5139:2019 section 4)'],
  ['pre-assembled', 'Pre-assembled BS (AS/NZS 5139:2019 section 5)'],
  ['other', 'Other (AS/NZS 5139:2019 section 6)'],
];
const INSTALL_LOCATIONS: [NonNullable<ItpBatteryEntry['installLocation']>, string][] = [
  ['internal', 'Internal'],
  ['external', 'External'],
];

function batterySystemType(value: string): BessSystemType | undefined {
  return BATTERY_SYSTEM_TYPES.some(([type]) => type === value)
    ? (value as BessSystemType)
    : undefined;
}

function batteryInstallLocation(value: string): ItpBatteryEntry['installLocation'] {
  return INSTALL_LOCATIONS.some(([location]) => location === value)
    ? (value as ItpBatteryEntry['installLocation'])
    : undefined;
}

const inverterNumbers: [keyof PvInverterEntry, string][] = [
  ['quantity', 'Quantity'],
  ['acPowerRatingW', 'AC power (W)'],
  ['acCurrentNominalA', 'AC nominal current (A)'],
  ['dcVoltageMpptLowerV', 'MPPT minimum voltage (V)'],
  ['dcVoltageMpptUpperV', 'MPPT maximum voltage (V)'],
  ['dcVoltageMaximumV', 'Maximum DC voltage (V)'],
  ['dcCurrentMaximumA', 'Maximum DC current (A)'],
];
const moduleNumbers: [keyof PvModuleEntry, string][] = [
  ['quantity', 'Quantity'],
  ['powerRatingW', 'Power per panel (W)'],
  ['voltageOpenCircuitV', 'Open-circuit voltage (V)'],
  ['currentShortCircuitA', 'Short-circuit current (A)'],
];
export function ReportHardwareEditor<T extends Pick<FieldReport, 'jobId' | 'hardware'>>({
  draft,
  onChange,
  onRowRemoved,
  onUploadPhoto,
  onDeletePhoto,
  onRotatePhoto,
  photos = [],
  pendingRemoval = null,
  conflicts = [],
  batteryInstallationType,
  suggestionReviewCount = 0,
  suggestionReviewFields = [],
}: {
  conflicts?: readonly HardwareConflict[];
  draft: T;
  onChange: (next: T) => void;
  /** Unticked accept-only photo readings. Zero hides the hardware-section count. */
  suggestionReviewCount?: number;
  suggestionReviewFields?: readonly string[];
  /** Feed install type (concurrent / subsequent). Shown, never written into a portal field. */
  batteryInstallationType?: string | null;
  /** A real row was removed. Callers re-index photo checks that point at later rows. */
  onRowRemoved?: (kind: HardwareKind, index: number) => void;
  photos?: readonly StoredPhotoManifest[];
  onOpenPhoto?: (photo: StoredPhotoManifest, url: string) => void;
  /** Open the confirm dialog immediately. The editor leaves this unset. */
  pendingRemoval?: { kind: HardwareKind; index: number } | null;
} & ReportPhotoActions) {
  const [reference, setReference] = useState<{
    photoId: string;
    url: string;
    name: string;
    mimeType: string;
    uploadedAt: string;
  } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [quarters, setQuarters] = useState(0);
  const [savingTurn, setSavingTurn] = useState(false);
  const [photoPrompt, setPhotoPrompt] = useState('');
  const rotateProp = onRotatePhoto ? { onRotatePhoto } : {};
  const [pending, setPending] = useState(pendingRemoval);
  const showReference = (photo: StoredPhotoManifest, url: string) => {
    setReference({
      photoId: photo.photoId,
      url,
      name: photo.filename,
      mimeType: photo.mimeType,
      uploadedAt: photo.uploadedAt,
    });
    setZoom(1);
    setQuarters(0);
  };
  useEffect(() => {
    const next = replacementReferencePhoto(reference, photos);
    if (!next) return;
    let cancelled = false;
    void fetchPhotoBlobUrl(next.jobId, next.photoId)
      .then((url) => {
        if (cancelled) {
          if (url.startsWith('blob:')) URL.revokeObjectURL(url);
          return;
        }
        if (
          !reference ||
          reference.photoId !== next.photoId ||
          reference.uploadedAt === next.uploadedAt
        ) {
          if (url.startsWith('blob:')) URL.revokeObjectURL(url);
          return;
        }
        const previousUrl = reference.url;
        setReference({
          photoId: next.photoId,
          url,
          name: next.filename,
          mimeType: next.mimeType,
          uploadedAt: next.uploadedAt,
        });
        if (previousUrl.startsWith('blob:') && previousUrl !== url)
          URL.revokeObjectURL(previousUrl);
        setQuarters(0);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [photos, reference]);
  async function saveReferenceTurn() {
    if (!reference || !onRotatePhoto) return;
    const degrees = quarterTurnDegrees(quarters);
    if (degrees === 0) return;
    setSavingTurn(true);
    try {
      await onRotatePhoto(reference.photoId, degrees);
    } finally {
      setSavingTurn(false);
    }
  }
  const pv = !draft.jobId.startsWith('BTY');
  const battery = !draft.jobId.startsWith('PVD');
  const breakerPortalWarning = battery
    ? mainSwitchBreakerPortalWarning(
        draft.hardware,
        suggestionReviewFields
          .filter((field) => field.includes('circuitBreakerSizeMainSwitch'))
          .map((field): AcceptOnlyReview => ({
            resultId: 'pending',
            field: field.replace(/^switchboard:/, ''),
            kind: 'batteries',
            surface: 'switchboard',
          })),
      )
    : undefined;
  const reviewFields = new Set(suggestionReviewFields);
  const batteryRows = draft.jobId.startsWith('BTY')
    ? seedBtyBatteryRow(draft.jobId, draft.hardware).batteries
    : draft.hardware.batteries.length
      ? draft.hardware.batteries
      : [{}];
  const labellingSuggestion =
    battery && 'checklist' in draft && Array.isArray(draft.checklist)
      ? switchboardLabellingSuggestion(draft.checklist)
      : undefined;
  const installTypeLabel = battery ? batteryInstallationLabel(batteryInstallationType) : undefined;
  const suggestedBatteryInverters =
    battery && draft.hardware.inverters.length === 0 ? resolveBatteryInverters(draft.hardware) : [];
  const batteryInverterRows = draft.hardware.inverters.length
    ? draft.hardware.inverters
    : suggestedBatteryInverters.length
      ? suggestedBatteryInverters
      : [{}];
  const showingBatteryInverterDefault = suggestedBatteryInverters.length > 0;
  const patch = (partial: Partial<FieldHardware>) => {
    const hardware = { ...draft.hardware, ...partial };
    if ('panelsInLongestString' in partial && partial.panelsInLongestString === undefined)
      hardware.totalArrayVoltageOpenCircuitV = undefined;
    if ('numberOfStrings' in partial && partial.numberOfStrings === undefined)
      hardware.totalArrayCurrentShortCircuitA = undefined;
    onChange({ ...draft, hardware: calculateArrayTotals(hardware) });
  };
  function updateRow<K extends 'inverters' | 'modules'>(
    kind: K,
    index: number,
    update: Partial<FieldHardware[K][number]>,
  ) {
    const rows = [...draft.hardware[kind]];
    rows[index] = { ...rows[index], ...update };
    patch({ [kind]: rows });
  }
  function updateBattery(index: number, update: Partial<FieldHardware['batteries'][number]>) {
    patch({
      batteries: withBatteryRowEdit(draft.jobId, draft.hardware, index, update).batteries,
    });
  }
  function updateBatteryInverter(index: number, update: Partial<PvInverterEntry>) {
    const base = draft.hardware.inverters.length
      ? draft.hardware.inverters
      : suggestedBatteryInverters.length
        ? suggestedBatteryInverters
        : [{}];
    patch({
      inverters: base.map((row, i) => (i === index ? { ...row, ...update } : { ...row })),
    });
  }
  function confirmRemoval() {
    if (!pending) return;
    const removed = pending;
    setPending(null);
    if (removed.kind === 'inverters' && battery && draft.hardware.inverters.length === 0) {
      patch({ inverters: [{}] });
      return;
    }
    const next = hardwareWithoutRow(draft.hardware, removed.kind, removed.index);
    if (!next) return;
    if (removed.kind === 'inverters' && battery && next.inverters.length === 0)
      next.inverters = [{}];
    onChange({ ...draft, hardware: next });
    onRowRemoved?.(removed.kind, removed.index);
  }
  return (
    <HardwareConflictsContext.Provider value={conflicts}>
      <div className="hardware-review-layout">
        <div className="edit-form-section">
          <h4>
            Hardware and system data
            {suggestionReviewCount > 0 && (
              <span className="hardware-suggestion-count" role="status">
                {acceptOnlyReviewLabel(suggestionReviewCount)}
              </span>
            )}
          </h4>
          <HardwareConflictValues conflicts={conflicts} />
          {pv && (
            <>
              <div className="form-grid">
                <NumberInput
                  label="Number of panels in longest string"
                  value={draft.hardware.panelsInLongestString}
                  integer
                  onChange={(panelsInLongestString) => patch({ panelsInLongestString })}
                />
                <NumberInput
                  label="Number of strings"
                  value={draft.hardware.numberOfStrings}
                  integer
                  onChange={(numberOfStrings) => patch({ numberOfStrings })}
                />
                {draft.hardware.modules.length > 1 && (
                  <label>
                    Module type for array calculation
                    <select
                      value={draft.hardware.arrayModuleIndex ?? ''}
                      onChange={(e) =>
                        patch({
                          arrayModuleIndex:
                            e.target.value === '' ? undefined : Number(e.target.value),
                        })
                      }
                    >
                      <option value="">Choose module type…</option>
                      {draft.hardware.modules.map((m, i) => (
                        <option key={i} value={i}>
                          {m.make} {m.model} (Module {i + 1})
                        </option>
                      ))}
                    </select>
                    Confirm this rating applies to the strings being calculated.
                  </label>
                )}
                <NumberInput
                  label="Total array open-circuit voltage (V)"
                  value={draft.hardware.totalArrayVoltageOpenCircuitV}
                  readOnly={draft.hardware.panelsInLongestString !== undefined}
                  onChange={(value) => patch({ totalArrayVoltageOpenCircuitV: value })}
                />
                <NumberInput
                  label="Total array short-circuit current (A)"
                  value={draft.hardware.totalArrayCurrentShortCircuitA}
                  readOnly={draft.hardware.numberOfStrings !== undefined}
                  onChange={(value) => patch({ totalArrayCurrentShortCircuitA: value })}
                />
              </div>
              <LabelPhotos
                jobId={draft.jobId}
                heading="Array photos"
                promptKey="pv-array-overview"
                photos={photos}
                onOpen={showReference}
                onUploadPhoto={onUploadPhoto}
                onDeletePhoto={onDeletePhoto}
                {...rotateProp}
              />
              {(draft.hardware.inverters.length ? draft.hardware.inverters : [{}]).map((inv, i) => (
                <HardwareConflictRow
                  key={i}
                  path={`inverters[${i}]`}
                  className="edit-hardware-fieldset edit-hardware-fieldset--inverter"
                >
                  <legend>Inverter {i + 1}</legend>
                  {draft.hardware.inverters.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setPending({ kind: 'inverters', index: i })}
                    >
                      Remove inverter {i + 1}
                    </button>
                  )}
                  <div className="form-grid">
                    {(['make', 'model', 'serial', 'ipRating'] as const).map((key) => (
                      <ConflictLabel key={key} path={key}>
                        {
                          {
                            make: 'Make',
                            model: 'Model',
                            serial: 'Serial number',
                            ipRating: 'IP rating',
                          }[key]
                        }
                        {key === 'serial' && reviewFields.has('inverters:serial') && (
                          <SuggestionReviewMark />
                        )}
                        <input
                          value={inv[key] ?? ''}
                          onChange={(e) => updateRow('inverters', i, { [key]: e.target.value })}
                        />
                      </ConflictLabel>
                    ))}
                    {inverterNumbers.map(([key, label]) => (
                      <NumberInput
                        key={key}
                        label={label}
                        value={inv[key] as number | undefined}
                        onChange={(value) => updateRow('inverters', i, { [key]: value })}
                      />
                    ))}
                    <ConflictLabel path="galvanicallyIsolated">
                      Galvanically isolated
                      <select
                        value={
                          inv.galvanicallyIsolated === undefined
                            ? ''
                            : String(inv.galvanicallyIsolated)
                        }
                        onChange={(e) =>
                          updateRow('inverters', i, {
                            galvanicallyIsolated:
                              e.target.value === '' ? undefined : e.target.value === 'true',
                          })
                        }
                      >
                        <option value="">Select…</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </ConflictLabel>
                  </div>
                  <LabelPhotos
                    jobId={draft.jobId}
                    heading={`Inverter ${i + 1} label photos`}
                    promptKey={
                      i === 0
                        ? 'pv-inverter-model-details'
                        : `inverter-row:${i}:pv-inverter-model-details`
                    }
                    matchKeys={inverterSectionPhotoKeys(i)}
                    photos={photos}
                    onOpen={showReference}
                    onUploadPhoto={onUploadPhoto}
                    onDeletePhoto={onDeletePhoto}
                    {...rotateProp}
                  />
                </HardwareConflictRow>
              ))}
              <button
                type="button"
                onClick={() =>
                  patch({
                    inverters: [
                      ...(draft.hardware.inverters.length ? draft.hardware.inverters : [{}]),
                      {},
                    ],
                  })
                }
              >
                Add inverter
              </button>
              {(draft.hardware.modules.length ? draft.hardware.modules : [{}]).map((mod, i) => (
                <HardwareConflictRow
                  key={i}
                  path={`modules[${i}]`}
                  className="edit-hardware-fieldset edit-hardware-fieldset--module"
                >
                  <legend>Module {i + 1}</legend>
                  {draft.hardware.modules.length > 0 && (
                    <button type="button" onClick={() => setPending({ kind: 'modules', index: i })}>
                      Remove module {i + 1}
                    </button>
                  )}
                  <div className="form-grid">
                    {(['make', 'model'] as const).map((key) => (
                      <ConflictLabel key={key} path={key}>
                        {key === 'make' ? 'Make' : 'Model'}
                        <input
                          value={mod[key] ?? ''}
                          onChange={(e) => updateRow('modules', i, { [key]: e.target.value })}
                        />
                      </ConflictLabel>
                    ))}
                    {moduleNumbers.map(([key, label]) => (
                      <NumberInput
                        key={key}
                        label={label}
                        value={mod[key] as number | undefined}
                        onChange={(value) => updateRow('modules', i, { [key]: value })}
                      />
                    ))}
                  </div>
                  <ConflictLabel path="serials">
                    Example module serial number
                    <input
                      value={mod.serials?.[0] ?? ''}
                      onChange={(e) =>
                        updateRow('modules', i, {
                          serials: e.target.value.trim() ? [e.target.value] : [],
                        })
                      }
                    />
                  </ConflictLabel>
                  <LabelPhotos
                    jobId={draft.jobId}
                    heading={`Module ${i + 1} label photos`}
                    promptKey="pv-module-model-details"
                    photos={photos}
                    onOpen={showReference}
                    onUploadPhoto={onUploadPhoto}
                    onDeletePhoto={onDeletePhoto}
                    {...rotateProp}
                  />
                  <LabelPhotos
                    jobId={draft.jobId}
                    heading="Panel serial number photo"
                    promptKey="pv-module-serial"
                    photos={photos}
                    onOpen={showReference}
                    onUploadPhoto={onUploadPhoto}
                    onDeletePhoto={onDeletePhoto}
                    {...rotateProp}
                  />
                </HardwareConflictRow>
              ))}
              <button
                type="button"
                onClick={() =>
                  patch({
                    modules: [
                      ...(draft.hardware.modules.length ? draft.hardware.modules : [{}]),
                      {},
                    ],
                  })
                }
              >
                Add module type
              </button>
              <IsolatorEditor
                title="AC isolators"
                rows={draft.hardware.acIsolators ?? []}
                onChange={(acIsolators) => patch({ acIsolators })}
                onRequestRemove={(index) => setPending({ kind: 'acIsolators', index })}
              />
              <LabelPhotos
                jobId={draft.jobId}
                heading="AC isolator photos"
                promptKey="pv-ac-isolator-rating"
                photos={photos}
                onOpen={showReference}
                onUploadPhoto={onUploadPhoto}
                onDeletePhoto={onDeletePhoto}
                {...rotateProp}
              />
              <LabelPhotos
                jobId={draft.jobId}
                heading="Switchboard AC isolator / main switch"
                promptKey="pv-main-switch-inverter-supply"
                photos={photos}
                onOpen={showReference}
                onUploadPhoto={onUploadPhoto}
                onDeletePhoto={onDeletePhoto}
                {...rotateProp}
              />
              <DcIsolatorEditor
                rows={draft.hardware.dcIsolators ?? []}
                onChange={(dcIsolators) => patch({ dcIsolators })}
                onRequestRemove={(index) => setPending({ kind: 'dcIsolators', index })}
              />
              <div className="hardware-photos">
                <h5>DC isolator photos</h5>
                {(draft.hardware.dcIsolators?.length ?? 0) > 1 && (
                  <p className="muted">
                    Photos are shared across this job’s DC isolators; check which isolator each
                    image shows.
                  </p>
                )}
                <div className="dc-isolator-evidence">
                  {(['before', 'during', 'after'] as const).map((stage) => (
                    <section key={stage}>
                      <LabelPhotos
                        heading={stage[0]!.toUpperCase() + stage.slice(1)}
                        jobId={draft.jobId}
                        promptKey={`pv-dc-isolator-${stage}`}
                        photos={photos}
                        onOpen={showReference}
                        onUploadPhoto={onUploadPhoto}
                        onDeletePhoto={onDeletePhoto}
                        {...rotateProp}
                      />
                    </section>
                  ))}
                </div>
              </div>
            </>
          )}
          {battery && (
            <fieldset className="edit-hardware-fieldset edit-hardware-fieldset--switchboard">
              <legend>Switchboard</legend>
              {breakerPortalWarning && (
                <p role="status" className="hardware-breaker-portal-warning">
                  {breakerPortalWarning}
                </p>
              )}
              <div className="form-grid">
                <CircuitBreakerSizeField
                  label="Size of circuit breaker controlling MAIN SWITCH (GRID)"
                  path="circuitBreakerSizeMainSwitchGrid"
                  review={reviewFields.has('switchboard:circuitBreakerSizeMainSwitchGrid')}
                  value={draft.hardware.circuitBreakerSizeMainSwitchGrid}
                  onChange={(circuitBreakerSizeMainSwitchGrid) =>
                    patch({ circuitBreakerSizeMainSwitchGrid })
                  }
                />
                <CircuitBreakerSizeField
                  label="Size of circuit breaker controlling Main Switch Inverter and/or Main Switch Alternative"
                  path="circuitBreakerSizeMainSwitchInverter"
                  review={reviewFields.has('switchboard:circuitBreakerSizeMainSwitchInverter')}
                  value={draft.hardware.circuitBreakerSizeMainSwitchInverter}
                  onChange={(circuitBreakerSizeMainSwitchInverter) =>
                    patch({ circuitBreakerSizeMainSwitchInverter })
                  }
                />
                <ConflictLabel path="alternativeSupplyFromBessInverter">
                  Alternative supply from the BESS/inverter
                  <input
                    type="checkbox"
                    checked={draft.hardware.alternativeSupplyFromBessInverter === true}
                    onChange={(e) => patch({ alternativeSupplyFromBessInverter: e.target.checked })}
                  />
                </ConflictLabel>
                <ConflictLabel path="switchboardLabellingRequirementsMet">
                  Switchboard and meter box labelling requirements met (AS/NZS 5139:2019)
                  <select
                    id="edit-switchboard-labelling"
                    aria-label="Switchboard labelling requirements met"
                    value={
                      draft.hardware.switchboardLabellingRequirementsMet === undefined
                        ? ''
                        : draft.hardware.switchboardLabellingRequirementsMet
                          ? 'yes'
                          : 'no'
                    }
                    onChange={(e) =>
                      patch({
                        switchboardLabellingRequirementsMet:
                          e.target.value === '' ? undefined : e.target.value === 'yes',
                      })
                    }
                  >
                    <option value="">Not confirmed</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  <span className="muted">
                    Confirm this before Approve for Portal. It is never set on its own.
                    {labellingSuggestion ? ` ${labellingSuggestionCopy(labellingSuggestion)}` : ''}
                  </span>
                </ConflictLabel>
              </div>
              {installTypeLabel && <p>Install type from the client feed: {installTypeLabel}</p>}
              <LabelPhotos
                jobId={draft.jobId}
                heading="Switchboard photos"
                promptKey="switchboard"
                photos={photos}
                onOpen={showReference}
                onUploadPhoto={onUploadPhoto}
                onDeletePhoto={onDeletePhoto}
                {...rotateProp}
              />
              <p className="muted">
                A switchboard photo can suggest the two breaker sizes. Accept each suggestion; a
                value already entered is kept.
              </p>
            </fieldset>
          )}
          {battery &&
            batteryRows.map((b, i) => (
              <HardwareConflictRow
                key={i}
                path={`batteries[${i}]`}
                className="edit-hardware-fieldset edit-hardware-fieldset--battery"
              >
                <legend>Battery {i + 1}</legend>
                <div className="form-grid">
                  {(['make', 'model', 'series', 'manufacturer', 'serial'] as const).map((key) => (
                    <ConflictLabel key={key} path={key}>
                      {HARDWARE_CHECK_LABELS[key]}
                      {reviewFields.has(`batteries:${key}`) && <SuggestionReviewMark />}
                      <input
                        value={b[key] ?? ''}
                        onChange={(e) => updateBattery(i, { [key]: e.target.value })}
                      />
                    </ConflictLabel>
                  ))}
                  <NumberInput
                    label="Capacity (kWh)"
                    review={reviewFields.has('batteries:capacityKwh')}
                    value={b.capacityKwh}
                    onChange={(value) => updateBattery(i, { capacityKwh: value })}
                  />
                  <ConflictLabel path="systemType">
                    Battery system type
                    <select
                      value={b.systemType ?? ''}
                      onChange={(e) =>
                        updateBattery(i, { systemType: batterySystemType(e.target.value) })
                      }
                    >
                      <option value="">Not entered</option>
                      {BATTERY_SYSTEM_TYPES.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </ConflictLabel>
                  <ConflictLabel path="installLocation">
                    Where is the battery installed?
                    <select
                      value={b.installLocation ?? ''}
                      onChange={(e) =>
                        updateBattery(i, {
                          installLocation: batteryInstallLocation(e.target.value),
                        })
                      }
                    >
                      <option value="">Not entered</option>
                      {INSTALL_LOCATIONS.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </ConflictLabel>
                  <ConflictLabel path="inverterSerial">
                    Serial of the inverter the battery connects to
                    {reviewFields.has('batteries:inverterSerial') && <SuggestionReviewMark />}
                    <input
                      value={b.inverterSerial ?? ''}
                      onChange={(e) => updateBattery(i, { inverterSerial: e.target.value })}
                    />
                    <span className="muted">
                      Filled on the portal for Pre-assembled BS (section 5) only.
                    </span>
                  </ConflictLabel>
                  {connectedInverterSerialSuggestion(draft.hardware, i) && (
                    <p>
                      <button
                        type="button"
                        onClick={() =>
                          updateBattery(i, {
                            inverterSerial: connectedInverterSerialSuggestion(draft.hardware, i),
                          })
                        }
                      >
                        Use inverter serial {connectedInverterSerialSuggestion(draft.hardware, i)}{' '}
                        for this all-in-one or same-vendor battery
                      </button>
                    </p>
                  )}
                </div>
                <LabelPhotos
                  jobId={draft.jobId}
                  heading={`Battery ${i + 1} label photos`}
                  promptKey={batteryLabelUploadKey(i)}
                  matchKeys={batterySectionPhotoKeys(i)}
                  photos={photos}
                  onOpen={showReference}
                  onUploadPhoto={onUploadPhoto}
                  onDeletePhoto={onDeletePhoto}
                  {...rotateProp}
                />
              </HardwareConflictRow>
            ))}
          {battery &&
            batteryInverterRows.map((inv, i) => (
              <HardwareConflictRow
                key={i}
                path={`inverters[${i}]`}
                className="edit-hardware-fieldset edit-hardware-fieldset--inverter"
              >
                <legend>Inverter {i + 1}</legend>
                {(draft.hardware.inverters.length > 0 || showingBatteryInverterDefault) && (
                  <button type="button" onClick={() => setPending({ kind: 'inverters', index: i })}>
                    Remove inverter {i + 1}
                  </button>
                )}
                {showingBatteryInverterDefault && (
                  <p className="muted">
                    Filled from the battery because no inverter was entered. Edit it, or remove it
                    to leave the inverter blank.
                  </p>
                )}
                <div className="form-grid">
                  {(
                    [
                      ['make', 'Make'],
                      ['model', 'Model'],
                      ['serial', 'Serial number'],
                    ] as const
                  ).map(([key, label]) => (
                    <ConflictLabel key={key} path={key}>
                      {label}
                      {key === 'serial' && reviewFields.has('inverters:serial') && (
                        <SuggestionReviewMark />
                      )}
                      <input
                        value={inv[key] ?? ''}
                        onChange={(e) => updateBatteryInverter(i, { [key]: e.target.value })}
                      />
                    </ConflictLabel>
                  ))}
                  <NumberInput
                    label="Quantity"
                    integer
                    value={inv.quantity}
                    onChange={(quantity) => updateBatteryInverter(i, { quantity })}
                  />
                </div>
                <LabelPhotos
                  jobId={draft.jobId}
                  heading={`Inverter ${i + 1} label photos`}
                  promptKey={
                    i === 0
                      ? 'inverter-inverter-label-and-serial-number'
                      : `inverter-row:${i}:inverter-inverter-label-and-serial-number`
                  }
                  matchKeys={inverterSectionPhotoKeys(i)}
                  photos={photos}
                  onOpen={showReference}
                  onUploadPhoto={onUploadPhoto}
                  onDeletePhoto={onDeletePhoto}
                  {...rotateProp}
                />
              </HardwareConflictRow>
            ))}
          {battery && (
            <button
              type="button"
              onClick={() =>
                patch({
                  inverters: [
                    ...(draft.hardware.inverters.length
                      ? draft.hardware.inverters
                      : suggestedBatteryInverters),
                    {},
                  ],
                })
              }
            >
              Add inverter
            </button>
          )}
          <h5>Hardware photos</h5>
          <label>
            Photo item
            <select
              value={photoPrompt}
              onChange={(e) => {
                setPhotoPrompt(e.target.value);
                setReference(null);
              }}
            >
              <option value="">Choose a photo item…</option>
              {getPromptsForJob(pv ? 'pv' : 'battery').map((prompt) => (
                <option key={prompt.promptKey} value={prompt.promptKey}>
                  {prompt.subject} · {prompt.shot}
                </option>
              ))}
            </select>
          </label>
          {photoPrompt && (
            <ReportPhotoEditor
              jobId={draft.jobId}
              promptKey={photoPrompt}
              photos={photos.filter((photo) => photo.promptKey === photoPrompt)}
              onOpenPhoto={showReference}
              onUploadPhoto={onUploadPhoto}
              onDeletePhoto={onDeletePhoto}
              {...rotateProp}
            />
          )}
          <label>
            <input
              type="checkbox"
              checked={draft.hardware.verifiedAgainstCer === true}
              onChange={(e) => patch({ verifiedAgainstCer: e.target.checked })}
            />{' '}
            Verified against installed equipment
          </label>
        </div>
        {pending && (
          <HardwareRemoveConfirm
            message={removeHardwareRowPrompt(pending.kind, pending.index)}
            onCancel={() => setPending(null)}
            onConfirm={confirmRemoval}
          />
        )}
        {reference && (
          <HardwareReferenceAside
            name={reference.name}
            url={reference.url}
            mimeType={reference.mimeType}
            zoom={zoom}
            quarters={quarters}
            saving={savingTurn}
            canRotate={Boolean(onRotatePhoto)}
            onZoom={setZoom}
            onClose={() => setReference(null)}
            onTurn={(delta) => setQuarters((current) => current + delta)}
            onSave={() => void saveReferenceTurn()}
          />
        )}
      </div>
    </HardwareConflictsContext.Provider>
  );
}

/** A stored photo whose bytes changed since the reference panel opened. */
export function replacementReferencePhoto<T extends { photoId: string; uploadedAt: string }>(
  current: { photoId: string; uploadedAt: string } | null,
  photos: readonly T[],
): T | null {
  if (!current) return null;
  const photo = photos.find((item) => item.photoId === current.photoId);
  if (!photo || photo.uploadedAt === current.uploadedAt) return null;
  return photo;
}

export function HardwareReferenceAside({
  name,
  url,
  mimeType,
  zoom,
  quarters,
  saving,
  canRotate,
  onZoom,
  onClose,
  onTurn,
  onSave,
}: {
  name: string;
  url: string;
  mimeType: string;
  zoom: number;
  quarters: number;
  saving: boolean;
  canRotate: boolean;
  onZoom: (zoom: number) => void;
  onClose: () => void;
  onTurn: (delta: number) => void;
  onSave: () => void;
}) {
  const turns = quarterTurnDegrees(quarters);
  return (
    <aside className="hardware-reference" aria-label="Hardware reference photo">
      <strong>{name}</strong>
      <button type="button" onClick={onClose}>
        Close photo
      </button>
      <label>
        Zoom
        <input
          aria-label="Hardware photo zoom"
          type="range"
          min="1"
          max="4"
          step="0.25"
          value={zoom}
          onChange={(e) => onZoom(Number(e.target.value))}
        />
      </label>
      {canRotate && canRotatePhoto(mimeType) && (
        <div className="hardware-reference__tools">
          <button
            type="button"
            disabled={saving}
            aria-label="Rotate left"
            onClick={() => onTurn(-1)}
          >
            Rotate ↺
          </button>
          <button
            type="button"
            disabled={saving}
            aria-label="Rotate right"
            onClick={() => onTurn(1)}
          >
            Rotate ↻
          </button>
          <button type="button" disabled={saving || turns === 0} onClick={onSave}>
            {saving ? 'Saving…' : 'Save orientation'}
          </button>
        </div>
      )}
      <div className="hardware-reference__image">
        <img
          src={url}
          alt={name}
          style={{
            width: `${zoom * 100}%`,
            maxWidth: 'none',
            ...(turns === 0 ? {} : { transform: `rotate(${turns}deg)` }),
          }}
        />
      </div>
    </aside>
  );
}

function LabelPhotos({
  heading,
  jobId,
  promptKey,
  matchKeys,
  photos,
  onOpen,
  onUploadPhoto,
  onDeletePhoto,
  onRotatePhoto,
}: {
  jobId: string;
  promptKey: string;
  /** Photos to show. New uploads still use `promptKey`. */
  matchKeys?: readonly string[];
  heading: string;
  photos: readonly StoredPhotoManifest[];
  onOpen: (photo: StoredPhotoManifest, url: string) => void;
} & ReportPhotoActions) {
  const keys = matchKeys ?? [promptKey];
  return (
    <div className="hardware-photos">
      <h5>{heading}</h5>
      <ReportPhotoEditor
        jobId={jobId}
        promptKey={promptKey}
        photos={photos.filter((photo) => keys.includes(photo.promptKey))}
        onOpenPhoto={onOpen}
        onUploadPhoto={onUploadPhoto}
        onDeletePhoto={onDeletePhoto}
        {...(onRotatePhoto ? { onRotatePhoto } : {})}
      />
    </div>
  );
}

export function HardwareRemoveConfirm({
  message,
  onConfirm,
  onCancel,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Confirm removal">
      <div className="modal__card">
        <p>{message}</p>
        <div className="modal__actions">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="danger" onClick={onConfirm}>
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function IsolatorEditor({
  title,
  rows,
  onChange,
  onRequestRemove,
}: {
  title: string;
  rows: readonly PvAcIsolatorEntry[];
  onChange: (rows: PvAcIsolatorEntry[]) => void;
  onRequestRemove: (index: number) => void;
}) {
  const visible = rows.length ? [...rows] : [{}];
  const update = (index: number, patch: Partial<PvAcIsolatorEntry>) => {
    const next = [...visible];
    next[index] = { insideSwitchboard: false, nextToInverter: false, ...next[index], ...patch };
    onChange(next);
  };
  return (
    <div>
      <h5>{title}</h5>
      {visible.map((row, index) => (
        <HardwareConflictRow
          key={index}
          path={`acIsolators[${index}]`}
          className="edit-hardware-fieldset edit-hardware-fieldset--isolator"
        >
          <legend>AC isolator {index + 1}</legend>
          <button type="button" onClick={() => onRequestRemove(index)}>
            Remove AC isolator {index + 1}
          </button>
          <NumberInput
            label="Rating (A)"
            value={row.ratingA}
            onChange={(ratingA) => update(index, { ratingA })}
          />
          <ConflictLabel path="insideSwitchboard">
            Inside switchboard
            <select
              value={row.insideSwitchboard === undefined ? '' : String(row.insideSwitchboard)}
              onChange={(e) =>
                update(index, {
                  insideSwitchboard: e.target.value === '' ? undefined : e.target.value === 'true',
                })
              }
            >
              <option value="">Select…</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </ConflictLabel>
          <ConflictLabel path="nextToInverter">
            Next to inverter
            <select
              value={row.nextToInverter === undefined ? '' : String(row.nextToInverter)}
              onChange={(e) =>
                update(index, {
                  nextToInverter: e.target.value === '' ? undefined : e.target.value === 'true',
                })
              }
            >
              <option value="">Select…</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </ConflictLabel>
        </HardwareConflictRow>
      ))}
      <button type="button" onClick={() => onChange([...visible, {}])}>
        Add AC isolator
      </button>
    </div>
  );
}

function DcIsolatorEditor({
  rows,
  onChange,
  onRequestRemove,
}: {
  rows: readonly PvDcIsolatorEntry[];
  onChange: (rows: PvDcIsolatorEntry[]) => void;
  onRequestRemove: (index: number) => void;
}) {
  const visible = (rows.length ? [...rows] : [{}]).map(defaultDcIsolator);
  const update = (index: number, patch: Partial<PvDcIsolatorEntry>) => {
    const next = [...visible];
    next[index] = { ...next[index], ...patch };
    onChange(next);
  };
  return (
    <div>
      <h5>DC isolators</h5>
      {visible.map((row, index) => (
        <HardwareConflictRow
          key={index}
          path={`dcIsolators[${index}]`}
          className="edit-hardware-fieldset edit-hardware-fieldset--isolator"
        >
          <legend>DC isolator {index + 1}</legend>
          <button type="button" onClick={() => onRequestRemove(index)}>
            Remove DC isolator {index + 1}
          </button>
          <div className="form-grid">
            <NumberInput
              label="Voltage (V)"
              value={row.voltageV}
              onChange={(voltageV) => update(index, { voltageV })}
            />
            <NumberInput
              label="Current (A)"
              value={row.currentA}
              onChange={(currentA) => update(index, { currentA })}
            />
            <ConflictLabel path="location">
              Location
              <select
                value={row.location ?? ''}
                onChange={(e) =>
                  update(index, {
                    location: (e.target.value || undefined) as PvDcIsolatorEntry['location'],
                  })
                }
              >
                <option value="">Select…</option>
                {['Adjacent to inverter', 'Integrated', 'Rooftop', 'Other'].map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </ConflictLabel>
            {row.location === 'Other' && (
              <ConflictLabel path="locationOther">
                Other location
                <input
                  value={row.locationOther ?? ''}
                  onChange={(e) => update(index, { locationOther: e.target.value })}
                />
              </ConflictLabel>
            )}
          </div>
          <ConflictLabel path="polarised">
            Polarised
            <select
              value={row.polarised === undefined ? '' : String(row.polarised)}
              onChange={(e) =>
                update(index, {
                  polarised: e.target.value === '' ? undefined : e.target.value === 'true',
                })
              }
            >
              <option value="">Select…</option>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </ConflictLabel>
        </HardwareConflictRow>
      ))}
      <button type="button" onClick={() => onChange([...visible, defaultDcIsolator()])}>
        Add DC isolator
      </button>
    </div>
  );
}
function CircuitBreakerSizeField({
  label,
  path,
  value,
  onChange,
  review = false,
}: {
  label: string;
  path: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  review?: boolean;
}) {
  const shown = (raw: string | undefined) => {
    if (!raw) return '';
    const normalized = normalizeCircuitBreakerSize(raw);
    return normalized.ok ? normalized.value : raw;
  };
  const [draft, setDraft] = useState(shown(value));
  const [error, setError] = useState('');
  useEffect(() => {
    setDraft(shown(value));
    setError('');
  }, [value]);
  return (
    <ConflictLabel path={path}>
      {label}
      {review && <SuggestionReviewMark />}
      <input
        inputMode="decimal"
        aria-label={label}
        value={draft}
        aria-invalid={error ? true : undefined}
        onChange={(e) => {
          const raw = e.target.value;
          if (!raw.trim()) {
            setDraft('');
            setError('');
            onChange(undefined);
            return;
          }
          const normalized = normalizeCircuitBreakerSize(raw);
          if (normalized.ok && normalized.value) {
            setError('');
            setDraft(normalized.value);
            onChange(normalized.value);
            return;
          }
          setDraft(raw);
          setError(normalized.ok ? '' : normalized.reason);
        }}
        onBlur={() => {
          if (!error) return;
          setDraft(shown(value));
          setError('');
        }}
      />
      {error && <span role="alert">{error}</span>}
    </ConflictLabel>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  integer = false,
  readOnly = false,
  review = false,
}: {
  integer?: boolean;
  readOnly?: boolean;
  review?: boolean;
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}) {
  return (
    <ConflictLabel path={numberFieldPath(label)}>
      {label}
      {review && <SuggestionReviewMark />}
      <input
        type="number"
        readOnly={readOnly}
        min={0}
        step={integer || label === 'Quantity' ? 1 : 'any'}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      />
    </ConflictLabel>
  );
}

function numberFieldPath(label: string): string {
  const aliases: Record<string, string> = {
    'AC power (W)': 'acPowerRatingW',
    'AC nominal current (A)': 'acCurrentNominalA',
    'MPPT minimum voltage (V)': 'dcVoltageMpptLowerV',
    'MPPT maximum voltage (V)': 'dcVoltageMpptUpperV',
    'Maximum DC voltage (V)': 'dcVoltageMaximumV',
    'Maximum DC current (A)': 'dcCurrentMaximumA',
    'Power per panel (W)': 'powerRatingW',
    'Open-circuit voltage (V)': 'voltageOpenCircuitV',
    'Short-circuit current (A)': 'currentShortCircuitA',
    'Capacity (kWh)': 'capacityKwh',
    'Rating (A)': 'ratingA',
    'Voltage (V)': 'voltageV',
    'Current (A)': 'currentA',
    'Number of panels in longest string': 'panelsInLongestString',
    'Total array open-circuit voltage (V)': 'totalArrayVoltageOpenCircuitV',
    'Total array short-circuit current (A)': 'totalArrayCurrentShortCircuitA',
  };
  return (
    aliases[label] ??
    Object.keys(FIELD_LABELS).find(
      (key) => FIELD_LABELS[key]?.toLowerCase() === label.toLowerCase(),
    ) ??
    label
  );
}
